/** @jsxImportSource @emotion/react */
import React, { memo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '../../../../components/button/button.component';
import { Notification } from '../../../../components/notification/notification.component';
import { formatMinutes } from '../../../../utils/utils';

import setDurationStyles from './set-duration.styles';
import { ISetUpState } from '../../set-up.types';
import { SecurityDeposit } from '../security-deposit/security-deposit.component';

type PropTypes = {
    priceHour: number;
    currency: string;
    onSetSetUpStep: (state: ISetUpState) => void;
    isCarConnected: boolean;
    isFree?: boolean;
    blockingFee?: number;
    hasSecurityDeposit?: boolean;
    showChargingLimit: boolean;
    setShowChargingLimit: (value: boolean) => void;
    securityDepositAmount?: number;
    isCreatingTransaction?: boolean;
    isUnavailable: boolean;
};

function SetDuration({
    // priceHour,
    blockingFee = 0,
    currency,
    onSetSetUpStep,
    isCarConnected,
    isFree = false,
    hasSecurityDeposit = false,
    showChargingLimit,
    setShowChargingLimit,
    securityDepositAmount,
    isCreatingTransaction,
    isUnavailable,
}: PropTypes) {
    const maxDuration = 4 * 60;
    const intl = useIntl();

    const [duration, setDuration] = useState<number>(60);

    const updateDuration = (value: number) => {
        setDuration(prevState => {
            return Math.min(maxDuration, Math.max(5, prevState + value));
        });
    };

    return (
        <div css={setDurationStyles.container}>
            <div css={setDurationStyles.columnWrapper}>
                <div css={[setDurationStyles.column, { alignItems: 'center', flexGrow: 3 }]}></div>
                <div
                    css={[
                        setDurationStyles.column,
                        { alignItems: 'center', flexGrow: 3, marginRight: 6 },
                    ]}>
                    <div css={setDurationStyles.durationBlock}>
                        <div css={setDurationStyles.durationValue}>
                            <div css={setDurationStyles.infoBlock}>
                                <Button
                                    type={'key'}
                                    text={'+'}
                                    onClick={() => updateDuration(60)}
                                />
                            </div>
                            <div css={setDurationStyles.durationValueLabel}>
                                {formatMinutes(duration, intl.formatMessage).hours}
                            </div>
                            <div css={setDurationStyles.infoBlock}>
                                <Button
                                    type={'key'}
                                    text={'–'}
                                    onClick={() => updateDuration(duration <= 60 ? 0 : -60)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    css={[
                        setDurationStyles.column,
                        { alignItems: 'center', flexGrow: 3, marginLeft: 6 },
                    ]}>
                    <div css={setDurationStyles.durationBlock}>
                        <div css={setDurationStyles.durationValue}>
                            <div css={setDurationStyles.infoBlock}>
                                <Button
                                    type={'key'}
                                    text={'+'}
                                    onClick={() => updateDuration(duration === 5 ? 5 : 10)}
                                />
                            </div>
                            <div css={setDurationStyles.durationValueLabel}>
                                {formatMinutes(duration, intl.formatMessage).minutes}
                            </div>
                            <div css={setDurationStyles.infoBlock}>
                                <Button
                                    type={'key'}
                                    text={'–'}
                                    onClick={() => updateDuration(-10)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div css={[setDurationStyles.column, { alignItems: 'center', flexGrow: 3 }]}></div>
            </div>

            <div css={setDurationStyles.blockingFeeBlock}>
                <span css={setDurationStyles.blockingFee}>
                    {intl.formatMessage({ id: 'connector.descriptions.blockingFee' })}
                </span>
                <span css={setDurationStyles.blockingFeeValue}>{`${Number(
                    blockingFee / 100
                ).toFixed(2)} ${currency}/${intl.formatMessage({ id: 'min' })}`}</span>
            </div>
            {!isFree && (
                <div css={setDurationStyles.blockingFeeBlock}>
                    <span css={setDurationStyles.blockingFee}>
                        {intl.formatMessage({ id: 'connector.descriptions.minimalAmount' })}
                    </span>
                    <span css={setDurationStyles.blockingFeeValue}>{`${Number(1).toFixed(
                        2
                    )} ${currency}`}</span>
                </div>
            )}

            {hasSecurityDeposit && (
                <SecurityDeposit
                    showChargingLimit={showChargingLimit}
                    setShowChargingLimit={setShowChargingLimit}
                    isCarConnected={isCarConnected}
                    onSetSetUpStep={onSetSetUpStep}
                    amount={securityDepositAmount}
                    currency={currency}
                    isCreatingTransaction={isCreatingTransaction}
                    isUnavailable={isUnavailable}
                />
            )}

            <div css={setDurationStyles.actionWrapper}>
                <Button
                    isLoading={isCreatingTransaction}
                    disabled={!isCarConnected || isCreatingTransaction || isUnavailable}
                    css={setDurationStyles.nextButton}
                    text={intl.formatMessage({ id: 'next' })}
                    onClick={() => {
                        onSetSetUpStep({ duration: Number(duration) });
                    }}
                />
            </div>

            <Notification
                text={intl.formatMessage({
                    id: isCarConnected
                        ? 'setEnergy.infoBanner.text'
                        : 'setEnergy.infoBanner.text.notConnected',
                })}
            />
        </div>
    );
}

export default memo(SetDuration);
